import { Box } from '@material-ui/core';
import { Currency, formatMoney, getMoneySymbolByCurrency, Loan } from '@nemuru-eng/js-api-client';
import { LoanCalculator, PricingFromCdn } from '@nemuru-eng/js-api-client/slim';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { NumberFormField } from '@/components/shared/components/NewContextForms/NumberFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { LoanConditionsDisplay } from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/components/ChangeLoanConditionsDialog/components/LoanConditionsDisplay';
import { PricingFilters } from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/components/ChangeLoanConditionsDialog/components/PricingFilters';
import { SelectOptionsComponent } from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/components/ChangeLoanConditionsDialog/components/SelectOptionsComponent';
import { LoanConditionsCalculator } from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/components/ChangeLoanConditionsDialog/entities/LoanConditionsCalculator';
import { useChangeLoanConditions } from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/components/ChangeLoanConditionsDialog/hooks/useChangeLoanConditions';
import { ChangeLoanConditions } from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/components/ChangeLoanConditionsDialog/services/ChangeLoanConditions';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';

interface ChangeLoanConditionsFormProps {
  loan: Loan;
  pricings: PricingFromCdn[];
}

export const ChangeLoanConditionsForm = ({ loan, pricings }: ChangeLoanConditionsFormProps) => {
  const LoanConditionsInstance = LoanConditionsCalculator.getInstance();
  const { watch, reset, resetField } = useFormContext();
  const { palette } = useCustomTheme();
  const [newLoanConditions, setNewLoanConditions] = useState<LoanCalculator | undefined>(undefined);
  const { filters, setFilters, loading, lenderNames, sortedPricings } = useChangeLoanConditions({
    pricings,
  });

  const selectedPricingId = watch('pricingId');
  const selectedTerm = watch('term');
  const selectedPrincipal = watch('principal');

  const selectedPricing = ChangeLoanConditions.getPricingById(pricings, selectedPricingId);
  const selectOptions = ChangeLoanConditions.getPricingsSelectOptions(pricings, loan, filters);

  const currency = loan.loanConditions.currency;

  useEffect(() => {
    return () => {
      reset();
      LoanConditionsInstance.deleteLoanCalculator();
    };
  }, []);

  useEffect(() => {
    if (!selectOptions.find((option) => option.id === selectedPricingId)) {
      resetField('pricingId');
    }
  }, [filters]);

  useEffect(() => {
    if (selectedTerm && selectedPricing && ChangeLoanConditions.isTermWithinRange(selectedPricing, selectedTerm)) {
      try {
        LoanConditionsInstance.setLoanConditions(
          Number(selectedTerm),
          { value: Number(selectedPrincipal), currency: loan.loanConditions.currency },
          selectedPricing,
        ).then(() => setNewLoanConditions(LoanConditionsInstance.getLoanCalculator()));
      } catch (error) {
        setNewLoanConditions(undefined);
      }
      return;
    }
    setNewLoanConditions(undefined);
  }, [selectedPrincipal, selectedTerm, selectedPricing]);

  if (!sortedPricings)
    return (
      <Box>
        <Font variant="body2" color="textPrimary">
          <Text tId={'NO_PRICINGS_AVAILABLE_FOR_THE_GIVEN_CONDITIONS'} />
        </Font>
      </Box>
    );

  return (
    <>
      <Box display={'flex'} flexDirection={'column'} gridGap={32}>
        <Font variant="subtitle1" color="textPrimary">
          <Text tId={'SELECT_PRICING_DESCRIPTION'} />
        </Font>
        <Box
          position={'relative'}
          display={'flex'}
          width={'100%'}
          alignItems={'center'}
          gridGap={24}
          flexDirection={'row'}
        >
          <Box width={'65%'} display={'flex'} alignItems={'center'} gridGap={24}>
            <LoanConditionsDisplay
              titleTextId="CURRENT_LOAN_CONDITIONS"
              product={loan.loanConditions.product}
              program={loan.loanConditions.program.name}
              principal={amountToValueMoneyMapper(loan.loanConditions.principal)}
              period={loan.loanConditions.loanCalculator.term.period}
              annualInterest={loan.loanConditions.annualInterest}
              apr={loan.loanConditions.loanCalculator.tae}
              quota={
                loan.loanConditions.loanCalculator.userDefinedQuota
                  ? amountToValueMoneyMapper(loan.loanConditions.loanCalculator.userDefinedQuota)
                  : null
              }
              singleColumn
            />
            <Icon color={palette.grey[400]}>arrow_forward</Icon>
          </Box>
          <Box width={'35%'}>
            {newLoanConditions ? (
              <LoanConditionsDisplay
                titleTextId={'NEW_LOAN_CONDITIONS'}
                product={newLoanConditions.product}
                program={newLoanConditions.program}
                principal={newLoanConditions.principal}
                period={newLoanConditions.term}
                annualInterest={newLoanConditions.annualInterest ?? null}
                apr={newLoanConditions.apr()}
                quota={newLoanConditions.calculateQuota()}
                singleColumn
                noLabel
              />
            ) : (
              <>
                <Box p={2} textAlign={'center'}>
                  <Font variant="body2" color="textPrimary">
                    <Text
                      strong
                      tId={
                        selectedPricingId && selectedTerm && selectedPrincipal && !newLoanConditions
                          ? 'INVALID_OPTIONS_FOR_THE_CONDITIONS'
                          : 'SELECT_PRICING_PRINCIPAL_AND_TERM'
                      }
                    />
                  </Font>
                </Box>
              </>
            )}
          </Box>
        </Box>
        <Box display={'flex'} flexDirection={'column'} gridGap={8}>
          <SelectFormField
            label={'SELECT_PRICING'}
            name="pricingId"
            selectOptions={selectOptions.map((option) => ({
              value: option.id,
              display: <SelectOptionsComponent loading={loading} lenderNames={lenderNames} selectOption={option} />,
            }))}
            renderValue={(option) => {
              const selectedPricing = sortedPricings.find((pricing) => pricing.id === option);
              if (!selectedPricing) return <Box>{option}</Box>;

              return <Box>{selectedPricing?.name}</Box>;
            }}
            fieldVariant={FieldVariant.form}
          />
          <PricingFilters filters={filters} setFilters={setFilters} />
        </Box>
        {selectedPricing && (
          <Box display={'flex'} flexDirection={'row'} gridGap={32} height={'80px'}>
            <NumberFormField
              fieldVariant={FieldVariant.form}
              valueUnit={getMoneySymbolByCurrency(currency ?? Currency.EUR)}
              name={'principal'}
              label={'PRINCIPAL_VALUE_SELECTOR'}
              labelDynamicDataTexts={[
                formatMoney(selectedPricing.rangePrincipalMin),
                formatMoney(selectedPricing.rangePrincipalMax),
              ]}
              allowDecimals={true}
            />
            <NumberFormField
              name="term"
              label={selectedPricing.rangePeriodMin && selectedPricing.rangePeriodMax ? 'TERM_VALUE_SELECTOR' : 'TERM'}
              fieldVariant={FieldVariant.form}
              labelDynamicDataTexts={
                selectedPricing.rangePeriodMin && selectedPricing.rangePeriodMax
                  ? [selectedPricing.rangePeriodMin, selectedPricing.rangePeriodMax]
                  : []
              }
              allowDecimals={false}
            />
          </Box>
        )}
      </Box>
    </>
  );
};
